import {
  API_ROUTES,
  auditEventSchema,
  objectFactSchema,
  objectRelationSchema,
  pageSchema,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/**
 * The object spine: the two capabilities every business object shares.
 *
 * A timeline is the audit trail narrowed to one object; relationships are the
 * foreign-key graph described. Both are reads, nothing on an object page is
 * stored for the page's sake.
 */

export const objectKeys = {
  relationships: (organizationId: string, entityType: string, entityId: string) =>
    ['objects', organizationId, entityType, entityId, 'relationships'] as const,
  timeline: (organizationId: string, resourceType: string, resourceId: string) =>
    ['objects', organizationId, resourceType, resourceId, 'timeline'] as const,
  organizationTimeline: (organizationId: string) =>
    ['objects', organizationId, 'timeline'] as const,
} as const;

const auditPageSchema = pageSchema(auditEventSchema);

export function objectRelationshipsQuery(
  organizationId: string,
  entityType: string,
  entityId: string,
) {
  return queryOptions({
    queryKey: objectKeys.relationships(organizationId, entityType, entityId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.objects.relationships(organizationId, entityType, entityId),
        schema: z.array(objectRelationSchema),
        signal,
      }),
    staleTime: 30_000,
  });
}

export function objectSummaryQuery(organizationId: string, entityType: string, entityId: string) {
  return queryOptions({
    queryKey: ['objects', organizationId, entityType, entityId, 'summary'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.objects.summary(organizationId, entityType, entityId),
        schema: z.array(objectFactSchema),
        signal,
      }),
    staleTime: 60_000,
  });
}

export function objectTimelineQuery(
  organizationId: string,
  resourceType: string,
  resourceId: string,
) {
  return queryOptions({
    queryKey: objectKeys.timeline(organizationId, resourceType, resourceId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.audit.list(organizationId)}?resourceType=${encodeURIComponent(resourceType)}&resourceId=${encodeURIComponent(resourceId)}&limit=25`,
        schema: auditPageSchema,
        signal,
      }),
    staleTime: 30_000,
  });
}

export function organizationTimelineQuery(organizationId: string) {
  return queryOptions({
    queryKey: objectKeys.organizationTimeline(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.audit.list(organizationId)}?limit=100`,
        schema: auditPageSchema,
        signal,
      }),
    staleTime: 15_000,
  });
}
